import { Grid, Typography, useTheme, MenuItem, Menu, Button } from '../../../../../base-components';
import { useFormContext } from 'react-hook-form';
import { RootState, useAppSelector } from '../../../../../base-redux';
import { tablesSelectors } from '../../../../../../actions';
import { TableFormValues } from '../../../../../../common/';
import UpdateDataButton from './UpdateDataButton';
import TableToggle from './TableToggle';
import { useTranslation } from 'react-i18next';
import { IBulkAction, ITableState, ITableToggle } from '../../../../table.interfaces';
import { useState } from 'react';

interface IBottomActionsProps {
    bulkActions?: Array<IBulkAction>;
    tableState?: ITableState;
    toggles?: Array<ITableToggle>;
}

const BottomActions = ({ bulkActions = [], tableState, toggles = [] }: IBottomActionsProps) => {
    const { getValues } = useFormContext();
    const { t } = useTranslation();
    const tableId = getValues(TableFormValues.tableId);
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const rowsData = useAppSelector((state: RootState) =>
        tablesSelectors.getRowsData(state, tableId)
    );

    const selectedRowIds = tableState?.selectedRows ? Object.keys(tableState.selectedRows) : [];
    const hasSelectedRows = selectedRowIds.length > 0;
    const hasBulkActions = bulkActions.length > 0;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMenuItemClick = (action: IBulkAction) => {
        action.onExecute(selectedRowIds);
        handleClose();
    };

    return (
        <>
            <Grid
                display="flex"
                paddingInlineEnd={2}
                height="64px"
                alignItems="center"
                justifyContent="space-between"
            >
                <Grid display="flex" alignItems="center" gap={2}>
                    <Grid sx={{ paddingInlineStart: theme.spacing(3) }}>
                        <Typography
                            variant={'body2'}
                            fontWeight={'bold'}
                            color={theme.palette.grey[600]}
                        >
                            {` ${t('tablesBase.results')} (${rowsData.length || 0})`}
                        </Typography>
                    </Grid>
                    <Grid>
                        <UpdateDataButton />
                    </Grid>
                    {toggles.map((toggle) => (
                        <TableToggle key={toggle.key} toggle={toggle} />
                    ))}
                </Grid>
                {hasSelectedRows && hasBulkActions && (
                    <Grid display="flex" alignItems="center" gap={1} sx={{ paddingInlineEnd: theme.spacing(3) }}>
                        <Typography
                            variant={'body2'}
                            color={theme.palette.grey[600]}
                        >
                            {selectedRowIds.length} {t('tablesBase.selected')}
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            color="secondary"
                            onClick={handleClick}
                            sx={{
                                height: '32px',
                                minHeight: '32px',
                                fontSize: '14px',
                            }}
                        >
                            {t('tablesBase.bulkActions')}
                        </Button>
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                        >
                            {bulkActions.map((action) => (
                                <MenuItem
                                    key={action.id}
                                    onClick={() => handleMenuItemClick(action)}
                                >
                                    {action.label}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Grid>
                )}
            </Grid>
        </>
    );
};

export default BottomActions;
